// Shared scenario state (pathogen × region × horizon) so navigating between
// surfaces preserves the selection (architecture proposal §10.1). Surfaces
// opt in via useScenario(); un-migrated surfaces keep their own local state.
//
// CD R11 #2 adds a global `navLensPersona` so the nav-side persona lens can
// LIGHT a sub-group (e.g. Finance lights Economics) without coupling to the
// per-surface persona state in OperatorWorkspace / MinisterBriefingPack.
// Default 'none' = no cluster lit.

import { createContext, useContext, useState, type ReactNode } from 'react';

/** Nav-side persona lens (CD R11 #2). 'none' = no cluster lit; specific
 *  persona values match a sub-group's `SUB_GROUP_PERSONA` entry and light
 *  that cluster (border + brighter chip + ▲ badge). Only `finance-minister`
 *  has a lit cluster today (Economics); the union is intentionally open so
 *  more persona-cluster pairs can land without a type churn. */
export type NavLensPersona = 'none' | 'finance-minister';

export interface Scenario {
  pathogenId: string;
  regionId: string;
  durationDays: number;
  setPathogenId: (id: string) => void;
  setRegionId: (id: string) => void;
  setDurationDays: (d: number) => void;
  /** Currently-active nav-side persona lens (CD R11 #2). */
  navLensPersona: NavLensPersona;
  setNavLensPersona: (p: NavLensPersona) => void;
}

const ScenarioCtx = createContext<Scenario | null>(null);

export function ScenarioProvider({ children }: { children: ReactNode }) {
  const [pathogenId, setPathogenId] = useState('sars-cov-2-wildtype');
  const [regionId, setRegionId] = useState('usa');
  const [durationDays, setDurationDays] = useState(180);
  const [navLensPersona, setNavLensPersona] = useState<NavLensPersona>('none');
  return (
    <ScenarioCtx.Provider value={{
      pathogenId, regionId, durationDays,
      setPathogenId, setRegionId, setDurationDays,
      navLensPersona, setNavLensPersona,
    }}>
      {children}
    </ScenarioCtx.Provider>
  );
}

export function useScenario(): Scenario {
  const ctx = useContext(ScenarioCtx);
  if (!ctx) throw new Error('useScenario must be used within <ScenarioProvider>');
  return ctx;
}
